import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
const TrainingTable = ({ tableData, handleTableChange, onBack, loading, setLoading, formData, institutes, schemes, trainings, uploadedFilePath }) => {
  const navigate = useNavigate();
  const [batchRows, setBatchRows] = useState([]);
  const totalFormParticipants = parseInt(formData.participants) || 0;
  const trainingDays = parseInt(formData.days) || 0;

  // Initialize batch rows from tableData
  useEffect(() => {
    if (tableData && tableData.length > 0) {
      setBatchRows(tableData);
    }
  }, [tableData]);

  // Calculate to date based on from date and training days
  const calculateToDate = (fromDate) => {
    if (!fromDate || !trainingDays) return '';
    
    const date = new Date(fromDate);
    // Add training days - 1 (since the start date counts as day 1)
    date.setDate(date.getDate() + (trainingDays - 1));
    
    // Format date to YYYY-MM-DD for the input field
    return date.toISOString().split('T')[0];
  };

  // Handle date change for a specific row
  const handleDateChange = (id, field, value) => {
    const updatedRows = batchRows.map(row => {
      if (row.id === id) {
        // If the fromDate is changing, automatically calculate and update toDate
        if (field === 'fromDate') {
          return { 
            ...row, 
            [field]: value,
            toDate: calculateToDate(value)
          };
        }
        return { ...row, [field]: value };
      }
      return row;
    });
    
    setBatchRows(updatedRows);
  };

  // Handle participant count change for a specific row
  const handleParticipantChange = (id, value) => {
    // Make sure we only accept numbers
    const newValue = value.replace(/\D/g, '');
    const numericValue = newValue === '' ? 0 : parseInt(newValue);

    const updatedRows = batchRows.map(row => {
      if (row.id === id) {
        return { ...row, participants: numericValue };
      }
      return row;
    });
    
    setBatchRows(updatedRows);
  };

  // Get current total of participants
  const currentParticipantsTotal = batchRows.reduce(
    (sum, row) => sum + (parseInt(row.participants) || 0), 
    0
  );

  // Handle final submission
// Update this section in the handleSubmit function
// Update this section in the handleSubmit function
// In the handleSubmit function, modify the finalData object to include scheme_name
const handleSubmit = async () => {
  // Check if participants total matches the form value
  if (currentParticipantsTotal !== totalFormParticipants) {
    alert(`Batch participants total (${currentParticipantsTotal}) does not match overall participants count (${totalFormParticipants}). Please adjust the participant values.`);
    return;
  }
  
  setLoading(true);
  
  try {
    // Validate that all rows have dates selected
    const incompleteRows = batchRows.filter(row => !row.fromDate || !row.toDate);
    if (incompleteRows.length > 0) {
      alert('Please select From Date and To Date for all batches.');
      setLoading(false);
      return;
    }
    
    // Validate that all rows have participants entered
    const emptyParticipantRows = batchRows.filter(row => 
      row.participants === '' || isNaN(parseInt(row.participants)) || parseInt(row.participants) <= 0
    );
    if (emptyParticipantRows.length > 0) {
      alert('Please enter a valid number of participants for all batches.');
      setLoading(false);
      return;
    }
    
    // Get selected institute and scheme
    const selectedInstitute = institutes.find(institute => institute.id === parseInt(formData.instituteId));
    const selectedScheme = schemes.find(scheme => scheme.id === parseInt(formData.schemeId));
    
    // Get center_name from localStorage
    const center_name = localStorage.getItem('name') || '';
    
    // Format data for API
    const batches = batchRows.map(row => ({
      batchNo: row.batchNo,
      fromDate: row.fromDate,
      toDate: row.toDate,
      participants: row.participants
    }));
    
    // Combine form data with batch data in the format required by the API
    const clientileArray = JSON.parse(formData.clientile);

    // Combine form data with batch data in the format required by the API
const finalData = {
  institute: selectedInstitute ? selectedInstitute.code : '',
  scheme: selectedScheme ? selectedScheme.code : '',
  scheme_name: selectedScheme ? selectedScheme.name : '',
  title: formData.training,
  year: formData.timePeriod === '2nd-quarter' ? formData.year.replace('-', 's') : formData.year,
  month: formData.month,
  asDate: formData.asDate,
  asNumber: formData.asNumber,
  asFile: uploadedFilePath,
  center_name: center_name,
  stitle: formData.shortTitle.toUpperCase(),
  training_type: formData.type,
  clientile: clientileArray,
  days: formData.days,
  participants: formData.participants,
  batches: formData.batches,
  amount: formData.amountAllocated,
  institutionLocation: formData.institutionLocation || '', // ADD THIS LINE
  exposureLocation: formData.exposureLocation || '',       // ADD THIS LINE
  batchesData: batches,
  type: "training"
};
    
    console.log('Submitting training data:', finalData);
    
    // Submit the form data using the API service
    const response = await api.training.addTraining(finalData);
    
    if (response && !response.error) {
      alert('Training event added successfully!');
      navigate('/dashboard');
    } else {
      alert(response.message || 'Error adding training event');
    }
  } catch (error) {
    console.error('Error submitting form:', error);
    alert(error.message || 'Error submitting form. Please try again.');
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">Batch Details</h3>
      
        
        {/* Table with editable date and participant fields */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-3 text-left font-medium text-gray-700">
                  Batch No.
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left font-medium text-gray-700">
                  From Date
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left font-medium text-gray-700">
                  To Date
                </th>
                <th className="border border-gray-300 px-4 py-3 text-left font-medium text-gray-700">
                  Participants
                </th>
              </tr>
            </thead>
            <tbody>
              {batchRows.map((row) => (
                <tr key={row.id} className={row.id % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                  <td className="border border-gray-300 px-4 py-3 text-gray-800">
                    {row.batchNo}
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    <input
                      type="date"
                      value={row.fromDate}
                      onChange={(e) => handleDateChange(row.id, 'fromDate', e.target.value)}
                      className="w-full border border-gray-300 rounded py-2 px-3 text-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
  <input
    type="date"
    value={row.toDate}
    onChange={(e) => handleDateChange(row.id, 'toDate', e.target.value)}
    className="w-full border border-gray-300 rounded py-2 px-3 text-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
  />
</td>
                  <td className="border border-gray-300 px-4 py-3">
                    <input
                      type="text"
                      value={row.participants}
                      onChange={(e) => handleParticipantChange(row.id, e.target.value)}
                      className="w-full border border-gray-300 rounded py-2 px-3 text-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter participants"
                    />
                  </td>
                </tr>
              ))}
              {/* Totals row */}
              <tr className="bg-blue-50 font-medium">
                <td className="border border-gray-300 px-4 py-3 text-gray-800" colSpan="3">
                  Total
                </td>
                <td className="border border-gray-300 px-4 py-3 text-center text-gray-800">
                  {currentParticipantsTotal}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Form action buttons */}
      <div className="pt-6 border-t border-gray-200">
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onBack}
            className="px-5 py-2.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="px-5 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center justify-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving Training...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Save Training
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrainingTable;